'use client'

import { Command as Cmdk } from 'cmdk'
import { Check, ChevronDown, Search } from 'lucide-react'
import { useCallback, useMemo, useRef, useState } from 'react'

import { cn } from '../cn'
import { Popover, PopoverContent, PopoverTrigger } from './popover'

export interface ComboboxOption {
  value: string
  label: string
  group?: string
  disabled?: boolean
}

export interface ComboboxProps {
  options: ComboboxOption[]
  value?: string
  onValueChange: (value: string) => void
  placeholder?: string
  searchPlaceholder?: string
  disabled?: boolean
  loading?: boolean
  emptyMessage?: string
  className?: string
  resetOnSelect?: boolean
  /**
   * Controlled search input. When provided together with `onSearchValueChange`,
   * the host owns the typed query — useful for server-side fuzzy search where
   * the option list is the response, not a static array. Pair with
   * `shouldFilter={false}` so cmdk doesn't double-filter the host's results.
   */
  searchValue?: string
  onSearchValueChange?: (value: string) => void
  /** Disable cmdk's built-in client-side filter. Default true. */
  shouldFilter?: boolean
}

const MAX_VISIBLE = 100

export function Combobox({
  options,
  value,
  onValueChange,
  placeholder = 'Select...',
  searchPlaceholder = 'Search...',
  disabled = false,
  loading = false,
  emptyMessage = 'No results found',
  className,
  resetOnSelect = false,
  searchValue,
  onSearchValueChange,
  shouldFilter = true,
}: ComboboxProps) {
  const [open, setOpen] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const selectedLabel = useMemo(() => {
    if (resetOnSelect || !value) {
      return null
    }
    return options.find((o) => o.value === value)?.label ?? null
  }, [options, value, resetOnSelect])

  const handleSelect = useCallback(
    (optionValue: string) => {
      onValueChange(optionValue)
      setOpen(false)
    },
    [onValueChange],
  )

  const groups = useMemo(() => {
    const hasGroups = options.some((o) => o.group)
    if (!hasGroups) {
      return null
    }
    const map = new Map<string, ComboboxOption[]>()
    for (const opt of options) {
      const group = opt.group ?? ''
      const existing = map.get(group)
      if (existing) {
        existing.push(opt)
      } else {
        map.set(group, [opt])
      }
    }
    return map
  }, [options])

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild disabled={disabled || loading}>
        <button
          type="button"
          aria-haspopup="listbox"
          aria-expanded={open}
          disabled={disabled || loading}
          className={cn(
            'flex h-10 w-full items-center justify-between gap-2 rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
            className,
          )}>
          <span className={cn('line-clamp-1', !selectedLabel && 'text-muted-foreground')}>
            {loading ? 'Loading...' : (selectedLabel ?? placeholder)}
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0"
        style={{ width: 'var(--radix-popover-trigger-width)' }}
        onOpenAutoFocus={(e) => {
          e.preventDefault()
          inputRef.current?.focus()
        }}>
        <Cmdk
          className="flex flex-col"
          shouldFilter={shouldFilter}
          filter={(value, search) => {
            const opt = options.find((o) => o.value === value)
            if (!opt) {
              return 0
            }
            return opt.label.toLowerCase().includes(search.toLowerCase()) ? 1 : 0
          }}>
          <div className="flex items-center border-b border-border px-3">
            <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
            <Cmdk.Input
              ref={inputRef}
              placeholder={searchPlaceholder}
              value={searchValue}
              onValueChange={onSearchValueChange}
              className="flex h-10 w-full bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground"
            />
          </div>
          <Cmdk.List className="max-h-[min(300px,50vh)] overflow-y-auto p-1">
            <Cmdk.Empty className="px-3 py-6 text-center text-sm text-muted-foreground">{emptyMessage}</Cmdk.Empty>
            {groups
              ? Array.from(groups.entries()).map(([groupName, groupOptions]) => (
                  <Cmdk.Group
                    key={groupName}
                    heading={groupName}
                    className="**:[[cmdk-group-heading]]:px-2 **:[[cmdk-group-heading]]:py-1.5 **:[[cmdk-group-heading]]:text-xs **:[[cmdk-group-heading]]:font-semibold **:[[cmdk-group-heading]]:text-muted-foreground">
                    {groupOptions.slice(0, MAX_VISIBLE).map((opt) => (
                      <Cmdk.Item
                        key={opt.value}
                        value={opt.value}
                        disabled={opt.disabled}
                        onSelect={handleSelect}
                        className="relative flex cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50">
                        <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                          {value === opt.value && <Check className="h-4 w-4" />}
                        </span>
                        <span className="truncate">{opt.label}</span>
                      </Cmdk.Item>
                    ))}
                  </Cmdk.Group>
                ))
              : options.slice(0, MAX_VISIBLE).map((opt) => (
                  <Cmdk.Item
                    key={opt.value}
                    value={opt.value}
                    disabled={opt.disabled}
                    onSelect={handleSelect}
                    className="relative flex cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50">
                    <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                      {value === opt.value && <Check className="h-4 w-4" />}
                    </span>
                    <span className="truncate">{opt.label}</span>
                  </Cmdk.Item>
                ))}
          </Cmdk.List>
        </Cmdk>
      </PopoverContent>
    </Popover>
  )
}
